/**
 * D5 — Ask User Modal (paged wizard).
 * Shows one question at a time with AI-suggested options + "Other" freetext.
 * The backend sends a `deep_ask_user` WS frame; the user answers each question
 * sequentially; the frontend sends `deep_ask_user_response` with all answers.
 */
import { useCallback, useEffect, useRef, useState } from "react"

import { useTranslation } from "react-i18next"

import type { AskUserPayload, QuestionItem } from "@/stores/ai-explorer-store"

import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Input } from "@/components/ui/input"
import { Textarea } from "@/components/ui/textarea"
import { logger } from "@/lib/logger"
import { cn } from "@/lib/utils"

export interface AskUserModalProps {
  open: boolean
  payload: AskUserPayload | null
  onSubmit: (answers: Record<string, string | string[]>) => void
  onSkip: () => void
}

function answerList(value: unknown): string[] {
  return Array.isArray(value)
    ? value.filter((item): item is string => typeof item === "string")
    : []
}

function progressStepClass(index: number, currentIndex: number): string {
  if (index < currentIndex) return "bg-primary"
  if (index === currentIndex) return "bg-primary/50"
  return "bg-muted"
}

// ── Component ─────────────────────────────────────────────────────────────────

export function AskUserModal({ open, payload, onSubmit, onSkip }: AskUserModalProps) {
  const { t } = useTranslation()
  const [answers, setAnswers] = useState<Record<string, string | string[]>>({})
  const [currentIndex, setCurrentIndex] = useState(0)
  const [otherText, setOtherText] = useState("")
  const otherInputRef = useRef<HTMLInputElement>(null)

  const questions = payload?.questions ?? []
  const currentQuestion = questions.at(currentIndex) ?? null
  const isLastQuestion = currentIndex === questions.length - 1
  const totalQuestions = questions.length

  // Reset state when modal opens with new payload
  useEffect(() => {
    if (open && payload) {
      const initial: Record<string, string | string[]> = {}
      for (const q of payload.questions) {
        if (q.type === "multi_choice") {
          initial[q.id] = []
        } else {
          initial[q.id] = q.default ?? ""
        }
      }
      setAnswers(initial)
      setCurrentIndex(0)

      setOtherText("")
    }
  }, [open, payload])

  const handleSelectOption = useCallback(
    (questionId: string, option: string, type: QuestionItem["type"]) => {
      setOtherText("")
      if (type === "multi_choice") {
        setAnswers((prev) => {
          const current = answerList(prev[questionId])
          const updated = current.includes(option)
            ? current.filter((v) => v !== option)
            : [...current, option]
          return { ...prev, [questionId]: updated }
        })
      } else {
        setAnswers((prev) => ({ ...prev, [questionId]: option }))
      }
    },
    []
  )

  const handleNext = useCallback(() => {
    let finalAnswers = answers

    if (currentQuestion?.type === "multi_choice" && otherText.trim()) {
      const current = answerList(finalAnswers[currentQuestion.id])
      if (!current.includes(otherText.trim())) {
        finalAnswers = { ...finalAnswers, [currentQuestion.id]: [...current, otherText.trim()] }
        setAnswers(finalAnswers)
      }
      setOtherText("")
    }

    if (isLastQuestion) {
      onSubmit(finalAnswers)
    } else {
      setCurrentIndex((i) => i + 1)
      setOtherText("")
    }
  }, [isLastQuestion, answers, onSubmit, currentQuestion, otherText])

  const handleBack = useCallback(() => {
    setCurrentIndex((i) => Math.max(0, i - 1))
    setOtherText("")
  }, [])

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLDivElement>) => {
      if (e.key === "Enter" && !(e.target instanceof HTMLTextAreaElement)) {
        e.preventDefault()
        handleNext()
      }
    },
    [handleNext]
  )

  // The backend defaults `questions` to `[]` when the agent's `ask_user` tool
  // args are malformed. Without this guard we'd render `null` for an open
  // modal, leaving the run blocked on `deep_ask_user_response` with no UI to
  // answer it (the "stuck at Waiting for your answers" symptom). Auto-skip
  // unblocks the queue and keeps the run moving.
  useEffect(() => {
    if (open && (!payload || !currentQuestion)) {
      logger.warn(
        `AskUserModal: open with no answerable payload — auto-skipping (hasPayload=${!!payload}, questionsCount=${questions.length})`
      )
      onSkip()
    }
  }, [open, payload, currentQuestion, questions.length, onSkip])

  if (!payload || !currentQuestion) return null

  const currentAnswer = answers[currentQuestion.id]
  const hasAnswer =
    currentQuestion.type === "multi_choice"
      ? answerList(currentAnswer).length > 0
      : Boolean(currentAnswer)

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        if (!o) onSkip()
      }}
    >
      <DialogContent className="sm:max-w-md" onKeyDown={handleKeyDown}>
        <DialogHeader>
          <DialogTitle>
            {t("deepExplorer.ask_user.question_of", {
              current: currentIndex + 1,
              total: totalQuestions,
            })}
          </DialogTitle>
          {currentIndex === 0 && payload.context && (
            <DialogDescription asChild>
              <p className="text-muted-foreground text-sm leading-relaxed">{payload.context}</p>
            </DialogDescription>
          )}
        </DialogHeader>

        {/* Progress bar */}
        <div className="flex gap-1">
          {questions.map((_, i) => (
            <div
              key={i}
              className={cn(
                "h-1 flex-1 rounded-full transition-colors",
                progressStepClass(i, currentIndex)
              )}
            />
          ))}
        </div>

        {/* Current question */}
        <div className="flex flex-col gap-3 py-2">
          <p className="text-foreground text-sm leading-snug font-medium">
            {currentQuestion.question}
          </p>

          {/* Suggested options */}
          {currentQuestion.options && currentQuestion.options.length > 0 && (
            <div className="flex flex-col gap-1.5">
              {currentQuestion.options.map((option) => {
                const isSelected =
                  currentQuestion.type === "multi_choice"
                    ? answerList(currentAnswer).includes(option)
                    : currentAnswer === option

                return (
                  <button
                    key={option}
                    type="button"
                    className={cn(
                      "flex items-center gap-2 rounded-md border px-3 py-2 text-left text-sm transition-colors",
                      "focus-visible:ring-ring focus-visible:ring-2 focus-visible:outline-none",
                      isSelected
                        ? "border-primary bg-primary/5 text-primary font-medium"
                        : "border-border bg-background text-foreground hover:bg-muted/50"
                    )}
                    onClick={() =>
                      handleSelectOption(currentQuestion.id, option, currentQuestion.type)
                    }
                  >
                    {currentQuestion.type === "multi_choice" && (
                      <span
                        className={cn(
                          "flex size-4 shrink-0 items-center justify-center rounded border text-xs",
                          isSelected
                            ? "border-primary bg-primary text-primary-foreground"
                            : "border-border"
                        )}
                      >
                        {isSelected && "✓"}
                      </span>
                    )}
                    {currentQuestion.type === "single_choice" && (
                      <span
                        className={cn(
                          "flex size-4 shrink-0 items-center justify-center rounded-full border",
                          isSelected ? "border-primary" : "border-border"
                        )}
                      >
                        {isSelected && <span className="bg-primary size-2 rounded-full" />}
                      </span>
                    )}
                    <span>{option}</span>
                  </button>
                )
              })}
            </div>
          )}

          {/* "Other" — inline freetext alternative to a suggested option.
              Only renders alongside choice questions; pure ``text`` questions
              with no options use the textarea below as the single input,
              otherwise we'd ship two redundant fields for the same answer. */}
          {currentQuestion.options && currentQuestion.options.length > 0 && (
            <div
              className={cn(
                "flex items-center gap-2 rounded-md border px-3 py-2 text-sm transition-[box-shadow,border-color,background-color] duration-200 ease-out",
                "focus-within:border-primary focus-within:shadow-[0_0_0_3px_hsl(var(--primary)/0.15)]",
                otherText ? "border-primary bg-primary/5" : "border-border border-dashed"
              )}
            >
              <span className="text-muted-foreground shrink-0 text-xs">
                {t("deepExplorer.ask_user.other_label")}
              </span>
              <Input
                ref={otherInputRef}
                // The wrapper above owns the focus glow (focus-within), so the
                // input must stay chromeless — `focus-visible:ring-0`, matching
                // how the glow is expressed on ui/Input.
                className="h-auto flex-1 border-0 bg-transparent p-0 text-sm shadow-none focus-visible:border-transparent focus-visible:ring-0"
                placeholder={t("deepExplorer.ask_user.type_own_answer")}
                type="text"
                value={otherText}
                onBlur={() => {
                  // For multi_choice, add on blur if there's text
                  if (currentQuestion.type === "multi_choice" && otherText.trim()) {
                    setAnswers((prev) => {
                      const current = answerList(prev[currentQuestion.id])
                      if (!current.includes(otherText.trim())) {
                        return { ...prev, [currentQuestion.id]: [...current, otherText.trim()] }
                      }
                      return prev
                    })
                  }
                }}
                onChange={(e) => {
                  const val = e.target.value
                  setOtherText(val)
                  // Directly set as the answer (deselect suggestions for single_choice)
                  if (currentQuestion.type !== "multi_choice") {
                    setAnswers((prev) => ({ ...prev, [currentQuestion.id]: val }))
                  }
                }}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.stopPropagation()
                    if (currentQuestion.type === "multi_choice" && otherText.trim()) {
                      setAnswers((prev) => {
                        const current = answerList(prev[currentQuestion.id])
                        if (!current.includes(otherText.trim())) {
                          return { ...prev, [currentQuestion.id]: [...current, otherText.trim()] }
                        }
                        return prev
                      })
                      setOtherText("")
                    }
                  }
                }}
              />
            </div>
          )}

          {/* Text-type questions: show a textarea instead of options */}
          {currentQuestion.type === "text" && !currentQuestion.options?.length && (
            <Textarea
              className="min-h-[80px] resize-y text-sm"
              placeholder={t("deepExplorer.ask_user.your_answer")}
              value={typeof currentAnswer === "string" ? currentAnswer : ""}
              onChange={(e) =>
                setAnswers((prev) => ({ ...prev, [currentQuestion.id]: e.target.value }))
              }
            />
          )}
        </div>

        <DialogFooter className="flex flex-row items-center gap-2 sm:justify-between">
          <div className="flex items-center gap-2">
            {currentIndex > 0 && (
              <Button size="sm" variant="ghost" onClick={handleBack}>
                {t("deepExplorer.ask_user.back")}
              </Button>
            )}
            <button
              className="text-muted-foreground hover:text-foreground rounded px-1 text-sm transition-colors"
              type="button"
              onClick={onSkip}
            >
              {t("deepExplorer.ask_user.skip_all")}
            </button>
          </div>
          <Button
            disabled={!hasAnswer && currentQuestion.type !== "text"}
            size="sm"
            onClick={handleNext}
          >
            {isLastQuestion ? t("deepExplorer.ask_user.submit") : t("deepExplorer.ask_user.next")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
